import { Box, Divider, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { priceFormatter } from "../../utils/Utility";

export type BreakdownNoteProps = {
  note: string;
  variant?: "default" | "orderTable";
};

/** Upfront accent used elsewhere in orders/invoices (softer than success green). */
const UPFRONT_ACCENT = "#00E5B0";

/** Order Details — line items use extended price; footer total is pay-now amount. */
export const UPFRONT_ORDER_DETAILS_NOTE =
  "*Please refer the invoice for any additional charges and applicable discount.";

export type UpfrontPriceBreakdownProps = {
  extendedSubtotal: number;
  discountAmount?: number;
  discount?: number | null;
  shippingCharges?: number;
  lateFees?: number;
  orderTotal?: number;
  convenienceFee?: number;
  total: number;
  /** Match order-details line-item column grid (label under Ordered Size, amount under Order Amount). */
  variant?: "default" | "orderTable";
  actionColumnMd?: number;
  /** Override the CSS grid template used in orderTable variant to match the parent row grid exactly. */
  gridTemplate?: string;
};

const AMOUNT_EPSILON = 0.005;

const orderTableGridColumns = (actionColumnMd: number) =>
  `1.5fr 1.5fr 1.5fr 1.5fr 2.5fr 0.5fr 1.5fr ${actionColumnMd}fr`;

const formatDiscountPercent = (pct: number): string =>
  Math.round(pct) === pct ? String(Math.round(pct)) : Number(pct.toFixed(1)).toString();

/** True when upfront order should show itemized breakdown instead of a single TOTAL row. */
export const hasUpfrontBreakdownContent = ({
  discountAmount = 0,
  shippingCharges = 0,
  lateFees = 0,
  convenienceFee = 0,
}: Pick<
  UpfrontPriceBreakdownProps,
  "discountAmount" | "shippingCharges" | "lateFees" | "convenienceFee"
>): boolean =>
  (discountAmount ?? 0) > AMOUNT_EPSILON ||
  (shippingCharges ?? 0) > AMOUNT_EPSILON ||
  (lateFees ?? 0) > AMOUNT_EPSILON ||
  (convenienceFee ?? 0) > AMOUNT_EPSILON;

type RowLineProps = {
  label: string;
  amount: number;
  showNegative?: boolean;
  bold?: boolean;
  discount?: boolean;
  variant: "default" | "orderTable";
  actionColumnMd: number;
  gridTemplate?: string;
};

const OrderTableRow = ({
  label,
  amount,
  showNegative = false,
  bold = false,
  discount = false,
  actionColumnMd,
  gridTemplate,
}: Omit<RowLineProps, "variant">) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr auto",
          md: gridTemplate ?? orderTableGridColumns(actionColumnMd),
        },
        columnGap: 2,
        alignItems: "center",
        py: 0.5,
      }}
    >
      <Typography
        variant={bold ? "h5" : "h6"}
        fontWeight={bold ? "bold" : 500}
        color={colors.grey[500]}
        sx={{ gridColumn: { xs: 1, md: 1 }, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      <Typography
        variant={bold ? "h5" : "h6"}
        fontWeight={bold ? "bold" : 500}
        color={discount ? UPFRONT_ACCENT : undefined}
        sx={{
          gridColumn: { xs: 2, md: 7 },
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {showNegative && amount > AMOUNT_EPSILON ? "−" : ""}
        {priceFormatter(amount)}
      </Typography>
    </Box>
  );
};

const DefaultRow = ({
  label,
  amount,
  showNegative = false,
  bold = false,
  discount = false,
}: Omit<RowLineProps, "variant" | "actionColumnMd">) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        width: "100%",
      }}
    >
      <Typography
        variant={bold ? "h5" : "h6"}
        color={colors.grey[500]}
        fontWeight={bold ? "bold" : 500}
      >
        {label}
      </Typography>
      <Typography
        variant={bold ? "h5" : "h6"}
        color={discount ? UPFRONT_ACCENT : undefined}
        fontWeight={bold ? "bold" : 500}
        sx={{ fontVariantNumeric: "tabular-nums", textAlign: "right", flexShrink: 0 }}
      >
        {showNegative && amount > AMOUNT_EPSILON ? "−" : ""}
        {priceFormatter(amount)}
      </Typography>
    </Box>
  );
};

const RowLine = (props: RowLineProps) =>
  props.variant === "orderTable" ? <OrderTableRow {...props} /> : <DefaultRow {...props} />;


const BreakdownNote = ({ note, variant }: BreakdownNoteProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        mt: variant === "orderTable" ? 0.75 : 1,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: `1px solid ${isDarkMode ? "rgba(255,183,77,0.45)" : "rgba(237,108,2,0.35)"}`,
        backgroundColor: isDarkMode ? "rgba(255,183,77,0.12)" : "rgba(255,244,229,0.95)",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: isDarkMode ? theme.palette.warning.light : theme.palette.warning.dark,
          fontWeight: 500,
          lineHeight: 1.5,
        }}
      >
        {variant === "default" ? `*${note}` : note}
      </Typography>
    </Box>
  );
};

/** Amber callout under Order Details total only (not used on invoice billing). */
export const UpfrontOrderDetailsNote = ({
  note = UPFRONT_ORDER_DETAILS_NOTE,
}: {
  note?: string;
}) => <BreakdownNote note={note} variant="orderTable" />;

/** CS-1845 — Shared upfront order / invoice price breakdown. */
const UpfrontPriceBreakdown = ({
  extendedSubtotal,
  discountAmount = 0,
  discount,
  shippingCharges = 0,
  lateFees = 0,
  orderTotal,
  convenienceFee = 0,
  total,
  variant = "default",
  actionColumnMd = 1.5,
  gridTemplate,
}: UpfrontPriceBreakdownProps) => {
  const discountPctLabel =
    discount != null && discount > 0
      ? ` (${formatDiscountPercent(discount)}%)`
      : "";
  const showDiscount = (discountAmount ?? 0) > AMOUNT_EPSILON;
  const showShipping = (shippingCharges ?? 0) > AMOUNT_EPSILON;
  const showLateFees = (lateFees ?? 0) > AMOUNT_EPSILON;
  const showConvenience = (convenienceFee ?? 0) > AMOUNT_EPSILON;

  const rowProps = { variant, actionColumnMd, gridTemplate };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: variant === "orderTable" ? 0.75 : 1.25,
        width: "100%",
      }}
    >
      {/* Show subtotal row whenever there is a discount OR shipping (so the breakdown makes sense) */}
      {(showDiscount || showShipping) && (
        <RowLine label="Subtotal" amount={extendedSubtotal} {...rowProps} />
      )}
      {showDiscount && (
        <RowLine
          label={`Discount${discountPctLabel}`}
          amount={discountAmount ?? 0}
          showNegative
          discount
          {...rowProps}
        />
      )}
      {showShipping && <RowLine label="Shipping" amount={shippingCharges} {...rowProps} />}
      {showLateFees && <RowLine label="Late Fees" amount={lateFees} {...rowProps} />}

      {(showDiscount || showShipping || showLateFees) && (
        <Divider sx={{ borderColor: "divider", my: variant === "orderTable" ? 1 : 0.25 }} />
      )}

      {showConvenience ? (
        <>
          <RowLine
            label="Order Total"
            amount={orderTotal ?? total - (convenienceFee ?? 0)}
            bold
            {...rowProps}
          />
          <RowLine label="Stripe Processing Fee" amount={convenienceFee ?? 0} {...rowProps} />
          <RowLine label="Total" amount={total} bold {...rowProps} />
        </>
      ) : (
        <RowLine label="Total" amount={total} bold {...rowProps} />
      )}
    </Box>
  );
};

/** Single TOTAL row for simple (non-itemized) cases. */
export const SimpleTotalRow = ({
  label = "TOTAL",
  amount,
  variant = "default",
  actionColumnMd = 1.5,
  gridTemplate,
}: {
  label?: string;
  amount: number;
  variant?: "default" | "orderTable";
  actionColumnMd?: number;
  gridTemplate?: string;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (variant === "orderTable") {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr auto",
            md: gridTemplate ?? orderTableGridColumns(actionColumnMd),
          },
          columnGap: 2,
          alignItems: "center",
          py: 0.5,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color={colors.grey[500]}
          sx={{ gridColumn: { xs: 1, md: 1 } }}
        >
          {label}
        </Typography>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            gridColumn: { xs: 2, md: 7 },
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {priceFormatter(amount)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        gap: 2,
      }}
    >
      <Typography variant="h5" fontWeight="bold">
        {label}
      </Typography>
      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{ fontVariantNumeric: "tabular-nums", textAlign: "right" }}
      >
        {priceFormatter(amount)}
      </Typography>
    </Box>
  );
};

export default UpfrontPriceBreakdown;
